// ===== SMac OS X window-manager store =====
// The "OS" is really just this reactive state: which windows exist, where they
// are, and which one is on top. Apps render off this. (See docs/ARCHITECTURE.md)

import { APPS, loadAppComponent } from './apps.js';
import { saveSession, loadSession } from './persist.svelte.js';
import { addRecent } from './recents.svelte.js';
import { cascadeLayout, tileLayout } from './window-layout.js';
import { trackAppOpen } from './visitor.svelte.js';
import { playOpen, playClose, playMinimize } from './sound.svelte.js';
import { untrack } from 'svelte';

const MENUBAR_H = 24;
const KEEP_VISIBLE = 80;

let counter = 0;

// Debounced auto-save — fires 400 ms after the last mutation.
let _saveTimer = null;
function scheduleSave() {
  clearTimeout(_saveTimer);
  _saveTimer = setTimeout(() => saveSession(windows, _spacesRef), 400);
}

// Spaces reference injected at boot (avoids circular import).
let _spacesRef = null;
export function injectSpacesRef(ref) {
  _spacesRef = ref;
}

/** Reactive list of open windows. */
export const windows = $state([]);

function topZ() {
  return windows.reduce((max, w) => Math.max(max, w.z), 10);
}

/** Open a new window. `component` is the Svelte component shown in the body. */
export function openWindow(opts = {}) {
  const id = ++counter;
  const offset = windows.length * 24;
  windows.push({
    id,
    appId: opts.appId ?? null,
    title: opts.title ?? 'Untitled',
    component: opts.component ?? null,
    props: opts.props ?? {},
    contentInset: opts.contentInset ?? 'standard',
    x: opts.x ?? 120 + offset,
    y: opts.y ?? 80 + offset,
    w: opts.w ?? 440,
    h: opts.h ?? null,
    minW: opts.minW ?? (opts.appId === 'projects' ? 560 : 320),
    minH: opts.minH ?? (opts.appId === 'projects' ? 360 : 220),
    z: topZ() + 1,
    minimized: false,
    spaceId: opts.spaceId ?? _spacesRef?.activeSpaceId ?? 1,
    fullscreen: false,
    splitSide: null,
    meta: opts.meta ?? {},
  });
  scheduleSave();
  // Guest Radar: record the app_open (no-op if tracking is inactive/opted out).
  if (opts.appId) trackAppOpen(opts.appId);
  return id;
}

/**
 * Restore windows from a persisted session snapshot.
 * Called once at boot before the desktop renders.
 */
export async function restoreSession(snapshot) {
  if (!snapshot?.windows?.length) return;
  // bump counter past all restored IDs
  const maxId = snapshot.windows.reduce((m, w) => Math.max(m, w.id ?? 0), 0);
  counter = maxId;

  let restoredZ = 10;
  for (const original of [...snapshot.windows].sort((a, b) => (a.z ?? 0) - (b.z ?? 0))) {
    // Migration: the old startup opened "About Me" as a TextEdit document.
    // Restore it as the dedicated About Me app so returning visitors get the
    // new notepad without having to reset their layout.
    const isLegacyAboutMe =
      original.appId === 'textedit' &&
      (original.title === 'About Me' ||
        String(original.props?.documentPath ?? '').includes('About Me'));
    const saved = isLegacyAboutMe
      ? { ...original, appId: 'about-me', title: 'About Me', props: {} }
      : original;
    const app = APPS.find((a) => a.id === saved.appId);
    if (!app) continue; // app removed from registry — skip
    const component = await loadAppComponent(app);
    const viewportW = globalThis.innerWidth || 1280;
    const viewportH = globalThis.innerHeight || 800;
    const width = Math.min(saved.w ?? app.w ?? 440, viewportW);
    const height = saved.h == null ? null : Math.min(saved.h, viewportH - MENUBAR_H);
    windows.push({
      id: saved.id,
      appId: saved.appId,
      title: saved.title ?? app.label,
      component,
      props: saved.props ?? app.props ?? {},
      contentInset: app.contentInset ?? 'standard',
      x: Math.min(
        Math.max(Number.isFinite(saved.x) ? saved.x : 120, KEEP_VISIBLE - width),
        viewportW - KEEP_VISIBLE
      ),
      y: Math.min(
        Math.max(Number.isFinite(saved.y) ? saved.y : 80, MENUBAR_H),
        viewportH - KEEP_VISIBLE
      ),
      w: width,
      h: height ?? app.h ?? null,
      minW: app.minW ?? (saved.appId === 'projects' ? 560 : 320),
      minH: app.minH ?? (saved.appId === 'projects' ? 360 : 220),
      z: ++restoredZ,
      minimized: saved.minimized ?? false,
      spaceId: saved.spaceId ?? 1,
      fullscreen: saved.fullscreen ?? false,
      splitSide: saved.splitSide ?? null,
      zoomed: saved.zoomed ?? false,
      prevX: saved.prevX,
      prevY: saved.prevY,
      prevW: saved.prevW,
      prevH: saved.prevH,
      meta: saved.meta ?? {},
    });
  }
}

/**
 * Launch an app from the registry. Single-instance: if its window already
 * exists we restore + focus it instead of opening a duplicate.
 */
export async function launchApp(app, props = {}, windowOptions = {}) {
  if (!app) return null;
  playOpen();
  addRecent({ kind: 'app', id: app.id, label: app.label, icon: app.icon });
  const existing = app.singleton !== false ? windows.find((w) => w.appId === app.id) : null;
  if (existing) {
    if (app.id === 'projects' && !existing.zoomed) {
      existing.w = Math.max(existing.w ?? 0, app.w ?? existing.w ?? 0);
      existing.h = Math.max(existing.h ?? 0, app.h ?? existing.h ?? 0);
    }
    existing.minimized = false;
    if (_spacesRef && existing.spaceId !== _spacesRef.activeSpaceId) {
      _spacesRef.activeSpaceId = existing.spaceId;
      _spacesRef.missionControlOpen = false;
    }
    focusWindow(existing.id);
    scheduleSave();
    return existing.id;
  }
  const component = await loadAppComponent(app);
  return openWindow({
    appId: app.id,
    title: app.label,
    component,
    props: { ...(app.props ?? {}), ...props },
    contentInset: app.contentInset,
    w: app.w,
    h: app.h,
    ...windowOptions,
  });
}

export function getWindow(id) {
  return windows.find((win) => win.id === id) ?? null;
}

export function windowsForApp(appId) {
  return windows.filter((win) => win.appId === appId).sort((a, b) => b.z - a.z);
}

export function restoreWindow(id) {
  const win = getWindow(id);
  if (!win) return false;
  win.minimized = false;
  focusWindow(id);
  scheduleSave();
  return true;
}

/**
 * Dock-style activation. Unlike launchApp, this prefers an existing window
 * even for apps that support multiple documents or browser windows.
 */
export async function activateApp(appOrId) {
  const app = typeof appOrId === 'string' ? APPS.find((entry) => entry.id === appOrId) : appOrId;
  if (!app) return null;
  const existing = windowsForApp(app.id)[0];
  if (existing) {
    restoreWindow(existing.id);
    return existing.id;
  }
  return launchApp(app);
}

export function minimizeWindow(id) {
  const win = windows.find((w) => w.id === id);
  if (win) {
    win.minimized = true;
    playMinimize();
    scheduleSave();
  }
}

export function closeWindow(id) {
  const i = windows.findIndex((w) => w.id === id);
  if (i !== -1) {
    windows.splice(i, 1);
    playClose();
    scheduleSave();
  }
}

export function minimizeApp(appId) {
  let changed = false;
  for (const win of windows) {
    if (win.appId !== appId || win.minimized) continue;
    win.minimized = true;
    changed = true;
  }
  if (changed) scheduleSave();
}

export function closeApp(appId) {
  let changed = false;
  for (let i = windows.length - 1; i >= 0; i -= 1) {
    if (windows[i].appId !== appId) continue;
    windows.splice(i, 1);
    changed = true;
  }
  if (changed) scheduleSave();
}

export function cycleAppWindows(appId, direction = 1) {
  const appWindows = windowsForApp(appId);
  if (!appWindows.length) return null;
  const currentId = activeId();
  const currentIndex = appWindows.findIndex((win) => win.id === currentId);
  const nextIndex =
    currentIndex === -1 ? 0 : (currentIndex + direction + appWindows.length) % appWindows.length;
  restoreWindow(appWindows[nextIndex].id);
  return appWindows[nextIndex].id;
}

/** Let an app store serialisable state on its window (survives reload). */
export function setWindowMeta(id, key, value) {
  untrack(() => {
    const win = windows.find((item) => item.id === id);
    if (!win) return;
    win.meta = { ...(win.meta ?? {}), [key]: value };
  });
  scheduleSave();
}

export function setWindowProps(id, props) {
  untrack(() => {
    const win = windows.find((item) => item.id === id);
    if (!win) return;
    win.props = { ...win.props, ...props };
  });
  scheduleSave();
}

export function zoomWindow(id) {
  const win = windows.find((w) => w.id === id);
  if (!win) return;
  if (!win.zoomed) {
    win.prevX = win.x;
    win.prevY = win.y;
    win.prevW = win.w;
    win.prevH = win.h;
    win.x = 0;
    win.y = MENUBAR_H;
    win.w = globalThis.innerWidth;
    win.h = globalThis.innerHeight - MENUBAR_H;
    win.zoomed = true;
  } else {
    win.x = win.prevX;
    win.y = win.prevY;
    win.w = win.prevW;
    win.h = win.prevH;
    win.zoomed = false;
  }
  scheduleSave();
}

/** Bring a window to the front. */
export function focusWindow(id) {
  const win = windows.find((w) => w.id === id);
  if (!win) return;
  if (_spacesRef && win.spaceId !== _spacesRef.activeSpaceId) {
    _spacesRef.activeSpaceId = win.spaceId;
    _spacesRef.missionControlOpen = false;
  }
  const highestOtherZ = windows.reduce(
    (max, item) => (item.id === id ? max : Math.max(max, item.z)),
    10
  );
  if (win.z > highestOtherZ) return;
  win.z = topZ() + 1;
  scheduleSave();
}

/** Id of the front-most (active) window, or null. */
export function activeId() {
  let id = null;
  let max = -1;
  for (const w of windows) {
    if (w.minimized) continue;
    if (_spacesRef && w.spaceId !== _spacesRef.activeSpaceId) continue;
    if (w.z > max) {
      max = w.z;
      id = w.id;
    }
  }
  return id;
}

export function moveWindow(id, x, y) {
  const win = windows.find((w) => w.id === id);
  if (!win) return;
  const maxX = globalThis.innerWidth - KEEP_VISIBLE;
  const maxY = globalThis.innerHeight - KEEP_VISIBLE;
  win.x = Math.min(Math.max(x, KEEP_VISIBLE - win.w), maxX);
  win.y = Math.min(Math.max(y, MENUBAR_H), maxY);
  scheduleSave();
}

export function resizeWindow(id, next) {
  const win = windows.find((w) => w.id === id);
  if (!win || win.zoomed) return;

  const minW = win.minW ?? 320;
  const minH = win.minH ?? 220;
  const viewportW = globalThis.innerWidth || 1200;
  const viewportH = globalThis.innerHeight || 800;

  let x = next.x ?? win.x;
  let y = next.y ?? win.y;
  let w = Math.max(next.w ?? win.w, minW);
  let h = Math.max(next.h ?? win.h ?? minH, minH);

  x = Math.min(Math.max(x, KEEP_VISIBLE - w), viewportW - KEEP_VISIBLE);
  y = Math.min(Math.max(y, MENUBAR_H), viewportH - KEEP_VISIBLE);
  w = Math.min(w, viewportW - x);
  h = Math.min(h, viewportH - y);

  win.x = x;
  win.y = y;
  win.w = Math.max(w, minW);
  win.h = Math.max(h, minH);
  scheduleSave();
}

export function enterFullscreen(id) {
  const win = windows.find((w) => w.id === id);
  if (!win) return;
  if (win.fullscreen) {
    win.x = win.prevX ?? 120;
    win.y = win.prevY ?? MENUBAR_H;
    win.w = win.prevW ?? 600;
    win.h = win.prevH ?? null;
    win.fullscreen = false;
    win.splitSide = null;
  } else {
    win.prevX = win.x;
    win.prevY = win.y;
    win.prevW = win.w;
    win.prevH = win.h;
    win.x = 0;
    win.y = MENUBAR_H;
    win.w = globalThis.innerWidth;
    win.h = globalThis.innerHeight - MENUBAR_H;
    win.fullscreen = true;
    win.splitSide = null;
    focusWindow(id);
  }
  scheduleSave();
}

export function exitWindowMode(id) {
  const win = windows.find((w) => w.id === id);
  if (!win) return false;
  if (win.fullscreen) {
    enterFullscreen(id);
    return true;
  }
  if (win.splitSide) {
    exitSplit(id);
    return true;
  }
  if (win.zoomed) {
    zoomWindow(id);
    return true;
  }
  return false;
}

export function enterSplitLeft(id) {
  const win = windows.find((w) => w.id === id);
  if (!win) return;
  // Bounce existing left occupant to the right side
  const leftOccupant = windows.find(
    (w) => w.id !== id && w.splitSide === 'left' && w.spaceId === win.spaceId
  );
  if (leftOccupant) enterSplitRight(leftOccupant.id);
  if (win.fullscreen) win.fullscreen = false;
  win.prevX = win.x;
  win.prevY = win.y;
  win.prevW = win.w;
  win.prevH = win.h;
  const W = globalThis.innerWidth;
  const H = globalThis.innerHeight - MENUBAR_H;
  win.x = 0;
  win.y = MENUBAR_H;
  win.w = Math.floor(W / 2);
  win.h = H;
  win.splitSide = 'left';
  focusWindow(id);
  scheduleSave();
}

export function enterSplitRight(id) {
  const win = windows.find((w) => w.id === id);
  if (!win) return;
  // Bounce existing right occupant to the left side
  const rightOccupant = windows.find(
    (w) => w.id !== id && w.splitSide === 'right' && w.spaceId === win.spaceId
  );
  if (rightOccupant) enterSplitLeft(rightOccupant.id);
  if (win.fullscreen) win.fullscreen = false;
  win.prevX = win.x;
  win.prevY = win.y;
  win.prevW = win.w;
  win.prevH = win.h;
  const W = globalThis.innerWidth;
  const H = globalThis.innerHeight - MENUBAR_H;
  win.x = Math.ceil(W / 2);
  win.y = MENUBAR_H;
  win.w = Math.floor(W / 2);
  win.h = H;
  win.splitSide = 'right';
  focusWindow(id);
  scheduleSave();
}

export function exitSplit(id) {
  const win = windows.find((w) => w.id === id);
  if (!win || !win.splitSide) return;
  win.x = win.prevX ?? 120;
  win.y = win.prevY ?? MENUBAR_H;
  win.w = win.prevW ?? 600;
  win.h = win.prevH ?? null;
  win.splitSide = null;
  scheduleSave();
}

export function moveWindowToSpace(id, spaceId) {
  const win = windows.find((w) => w.id === id);
  if (win) {
    win.spaceId = spaceId;
    scheduleSave();
  }
}

export function cascadeWindows(spaceId) {
  cascadeLayout(windows, spaceId ?? _spacesRef?.activeSpaceId ?? 1, MENUBAR_H);
  scheduleSave();
}

export function tileWindows(spaceId) {
  tileLayout(windows, spaceId ?? _spacesRef?.activeSpaceId ?? 1, MENUBAR_H);
  scheduleSave();
}
